import type { DotProps } from "@/v2-backoffice-client/components/dot"
import {
  summaryFraction,
  type ColumnMeta,
  type ColumnMetaMap,
  type GroupingRange,
} from "@/v2-backoffice-client/components/table"
import UsageScoreBadge from "@/v2-backoffice-client/usage/UsageScoreBadge"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import {
  getScoreLevel,
  getTrendLevel,
  SCORE_LEVEL_FILTER_OPTIONS,
  SCORE_LEVEL_LABELS,
  SCORE_LEVEL_ORDER,
  SCORE_TOOLTIP,
  TREND_LEVEL_FILTER_OPTIONS,
  TREND_TOOLTIP,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  CHURN_LEVEL_ORDER,
  CHURN_RISK_LABELS,
  deriveScopeUsageSignals,
  type ChurnRiskLevel,
  type UsageScopeStats,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"

/**
 * Shared usage-signal column builders. Every table that lists teams,
 * customers, or users — top-level boards and the nested panel tabs alike —
 * spreads these into its column map so the aggregates read identically
 * everywhere. Only the deep-link target differs per table (top-level links
 * replace the sheet, nested `>>` links stack a new one), so that is the one
 * thing callers provide.
 *
 * The filter configs work in BOTH table modes: the server-paged boards send
 * the column ids to the Go filter registries (usersFilterRegistry /
 * usagestats.AddScopeUsageFilters — ids and SQL semantics must stay in
 * sync), the client-side nested panel tables evaluate the same filters
 * against getValue via TanStack.
 */

const CHURN_RISK_DOT_VARIANTS: Partial<
  Record<ChurnRiskLevel, DotProps["variant"]>
> = {
  high: "destructive",
  medium: "warning",
  low: "success-2",
  new: "muted",
}

// 'none' (no seats) is noise as a filter — those rows show no badge.
const CHURN_RISK_FILTER_OPTIONS = CHURN_LEVEL_ORDER.map((level) => ({
  value: level,
  label: CHURN_RISK_LABELS[level],
  dotVariant: CHURN_RISK_DOT_VARIANTS[level],
}))

/**
 * Group-by buckets for the score column: the same level thresholds as the
 * score badges (getScoreLevel), so "Healthy" groups exactly the rows whose
 * badge reads Healthy. The column's getValue encodes "no score" as -1.
 */
const SCORE_GROUPING_RANGES: GroupingRange[] = SCORE_LEVEL_ORDER.map(
  (level) => ({
    label: SCORE_LEVEL_LABELS[level],
    test: (value) =>
      (typeof value === "number" && value >= 0
        ? getScoreLevel(value)
        : "new") === level,
  }),
)

type BuildScopeUsageColumnsInput<TData> = {
  /** Deep link for every usage cell: the scope's Activity tab. */
  buildTo(row: TData): string | undefined
  /**
   * The money the row's churn risk puts at stake, as a finished tooltip
   * sentence — appended to the churn badge's reasons on at-risk (high /
   * medium) rows. Customer rows pass their 12-month billed revenue; teams
   * carry no revenue and pass nothing.
   */
  getAtStake?(row: TData): string | null
}

/**
 * The four scope-level usage columns (Score, Active seats, Trend, Churn
 * risk) for team- and customer-shaped rows carrying `UsageScopeStats`.
 */
export function buildScopeUsageColumns<TData extends UsageScopeStats>(
  input: BuildScopeUsageColumnsInput<TData>,
): ColumnMetaMap<TData> {
  const { buildTo, getAtStake } = input
  const to: ColumnMeta<TData>["to"] = ({ row }) => buildTo(row.original)

  return {
    UsageAvgScore: {
      id: "UsageAvgScore",
      label: "Score",
      description:
        "Average engagement score (0–100) of the seats that were active in " +
        "the last 28 days — idle seats don't drag it down, the Active seats " +
        "column shows how many sit idle. " +
        SCORE_TOOLTIP,
      defaultSize: 130,
      // Rounded like the badge (and the server-side filter expression), so
      // "score >= 50" matches exactly the rows whose badge reads 50+.
      getValue: (row) => deriveScopeUsageSignals(row)?.avgScore ?? -1,
      groupingRanges: SCORE_GROUPING_RANGES,
      filter: { type: "number-range", placeholder: "0–100" },
      to,
      renderCell({ row }) {
        const signals = deriveScopeUsageSignals(row.original)
        if (signals?.avgScore == null) return null
        return <UsageScoreBadge score={signals.avgScore} />
      },
      exportValue({ row }) {
        return row.original.UsageAvgScore ?? ""
      },
      // The badge's score, not the -1 sort sentinel: a missing score is a
      // missing value, not a score to add up.
      summaryValue({ row }) {
        return deriveScopeUsageSignals(row.original)?.avgScore ?? null
      },
    },
    // Virtual column (never rendered — the score badge already shows the
    // level) powering the always-visible "Score level" toolbar quick
    // filter. Same thresholds as the badge (getScoreLevel), so "Healthy"
    // filters exactly the rows whose badge reads Healthy. Server-paged
    // boards compile it via usagestats.filters.go's ScoreLevel expression;
    // client-side nested tables evaluate getValue.
    ScoreLevel: {
      id: "ScoreLevel",
      label: "Score level",
      description: SCORE_TOOLTIP,
      virtual: true,
      enableSorting: false,
      getValue: (row) => getScoreLevel(deriveScopeUsageSignals(row)?.avgScore),
      filter: {
        type: "multi-select",
        placeholder: "Any level",
        toolbar: true,
        options: SCORE_LEVEL_FILTER_OPTIONS,
      },
    },
    UsageActiveSeats: {
      id: "UsageActiveSeats",
      label: "Active seats (28d)",
      description:
        "Seats with any tracked activity in the last 28 days, of all " +
        "licensed seats. The gap is idle licenses — the cheapest churn " +
        "defense is onboarding them before the renewal.",
      enableSorting: false,
      defaultSize: 110,
      getValue: (row) => row.UsageActiveCount ?? 0,
      filter: { type: "number-range" },
      to,
      renderCell({ row }) {
        const signals = deriveScopeUsageSignals(row.original)
        if (!signals || signals.seatCount === 0) return null
        return (
          <span className="tabular-nums">
            {signals.activeCount}/{signals.seatCount}
          </span>
        )
      },
      // Both numbers the cell shows, so the summary adds up active seats
      // AND seats ("48/152") and reads their share.
      summaryValue({ row }) {
        const signals = deriveScopeUsageSignals(row.original)
        return summaryFraction(signals?.activeCount, signals?.seatCount)
      },
    },
    UsageTrend: {
      id: "UsageTrend",
      label: "Trend",
      description: `Whole-scope activity trend. ${TREND_TOOLTIP}`,
      enableSorting: false,
      defaultSize: 80,
      getValue: (row) => deriveScopeUsageSignals(row)?.trendPct ?? 0,
      filter: { type: "number-range", placeholder: "% vs prev 28 d" },
      to,
      renderCell({ row }) {
        const signals = deriveScopeUsageSignals(row.original)
        if (!signals || signals.seatCount === 0) return null
        return <UsageTrendBadge trendPct={signals.trendPct} />
      },
      exportValue({ row }) {
        return deriveScopeUsageSignals(row.original)?.trendPct ?? ""
      },
      // The badge's trend, not the 0 sort sentinel: a scope with no
      // baseline has no trend, not a flat one.
      summaryValue({ row }) {
        const signals = deriveScopeUsageSignals(row.original)
        if (!signals || signals.seatCount === 0) return null
        return signals.trendPct
      },
    },
    // Same idea as ScoreLevel: quick threshold buckets over the scope
    // trend for the always-visible "Trend level" toolbar filter, displayed
    // inside the Trend column's badge instead of a column of its own.
    TrendLevel: {
      id: "TrendLevel",
      label: "Trend level",
      description: TREND_TOOLTIP,
      virtual: true,
      enableSorting: false,
      getValue: (row) => getTrendLevel(deriveScopeUsageSignals(row)?.trendPct),
      filter: {
        type: "multi-select",
        placeholder: "Any trend",
        toolbar: true,
        options: TREND_LEVEL_FILTER_OPTIONS,
      },
    },
    ChurnRisk: {
      id: "ChurnRisk",
      label: "Churn risk",
      description:
        "Rule-based read of the last 28 days: quiet or never-activated " +
        "seats, falling usage, low engagement. Hover a row's badge for the " +
        "exact reasons behind its level.",
      enableSorting: false,
      defaultSize: 130,
      getValue: (row) => deriveScopeUsageSignals(row)?.churnRisk.level ?? "",
      enableGrouping: true,
      renderGroupLabel: (value) =>
        value
          ? (CHURN_RISK_LABELS[value as ChurnRiskLevel] ?? String(value))
          : "No usage data",
      filter: {
        type: "multi-select",
        placeholder: "Any risk",
        toolbar: true,
        options: CHURN_RISK_FILTER_OPTIONS,
      },
      to,
      renderCell({ row }) {
        const signals = deriveScopeUsageSignals(row.original)
        if (!signals || signals.churnRisk.level === "none") return null
        const { level, reasons } = signals.churnRisk
        // The money rides only on at-risk badges — a healthy account's
        // revenue is a fact, not a warning.
        const atStake =
          level === "high" || level === "medium"
            ? (getAtStake?.(row.original) ?? null)
            : null
        return (
          <UsageChurnRiskBadge
            level={level}
            reasons={atStake ? [...reasons, atStake] : reasons}
          />
        )
      },
      exportValue({ row }) {
        return deriveScopeUsageSignals(row.original)?.churnRisk.level ?? ""
      },
      // The badge's wording, so a breakdown reads "9× Healthy" and a scope
      // without seats (no badge) counts as empty.
      summaryValue({ row }) {
        const signals = deriveScopeUsageSignals(row.original)
        if (!signals || signals.churnRisk.level === "none") return null
        return CHURN_RISK_LABELS[signals.churnRisk.level]
      },
    },
  }
}

/** The per-user usage slice the user columns read. */
type UserUsageSource = {
  EngagementScore?: number | null
  UsageTrendPct?: number | null
}

type BuildUserUsageColumnsInput<TData> = {
  /** Adapts the row to its usage slice (identity, or e.g. `row.User`). */
  getUsage(row: TData): UserUsageSource | null | undefined
  /** Deep link for both cells: the user panel's Usage tab. */
  buildTo(row: TData): string | undefined
}

/** The two per-user usage columns (Score, Trend) for user-shaped rows. */
export function buildUserUsageColumns<TData>(
  input: BuildUserUsageColumnsInput<TData>,
): ColumnMetaMap<TData> {
  const { getUsage, buildTo } = input
  const to: ColumnMeta<TData>["to"] = ({ row }) => buildTo(row.original)

  return {
    EngagementScore: {
      id: "EngagementScore",
      label: "Score",
      description: SCORE_TOOLTIP,
      defaultSize: 130,
      getValue: (row) => getUsage(row)?.EngagementScore ?? -1,
      filter: { type: "number-range", placeholder: "0–100" },
      to,
      renderCell({ row }) {
        const usage = getUsage(row.original)
        if (usage?.EngagementScore == null) return null
        return <UsageScoreBadge score={usage.EngagementScore} />
      },
      exportValue({ row }) {
        return getUsage(row.original)?.EngagementScore ?? ""
      },
      // The score itself, not the -1 sort sentinel.
      summaryValue({ row }) {
        return getUsage(row.original)?.EngagementScore ?? null
      },
    },
    UsageTrendPct: {
      id: "UsageTrendPct",
      label: "Trend",
      description: TREND_TOOLTIP,
      defaultSize: 80,
      getValue: (row) => getUsage(row)?.UsageTrendPct ?? 0,
      filter: { type: "number-range", placeholder: "% vs prev 28 d" },
      to,
      renderCell({ row }) {
        const usage = getUsage(row.original)
        if (usage?.UsageTrendPct == null && usage?.EngagementScore == null) {
          return null
        }
        return <UsageTrendBadge trendPct={usage.UsageTrendPct} />
      },
      exportValue({ row }) {
        return getUsage(row.original)?.UsageTrendPct ?? ""
      },
      // The trend itself, not the 0 sort sentinel.
      summaryValue({ row }) {
        return getUsage(row.original)?.UsageTrendPct ?? null
      },
    },
  }
}
